
import { useState, useContext, useEffect } from 'react'
import { FloatingLabel, Form, Button } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { get, post, put } from '../api/rest'
import { GlobalContext } from '../App'
import Template, { Content } from '../components/Template'
import ReportInfo from '../components/ReportInfo'
import ChannelHeader from '../components/ChannelHeader'
import { ANONYMOUS_EMAIL, ANONYMOUS_HANDLE, UserAvatar } from '../components/helpers'
import { UserDisplay } from '../components/LoginModal'

export default function Success() {
  const global = useContext(GlobalContext)
  const [loading, setLoading] = useState(false)
  const { reportId } = useParams()

  /**
   * Get the report created in previous screen
   */
  useEffect(() => {
    const getReport = async () => {
      const token = global.getToken()
      // get the report information
      const reportPromise = get('report/' + reportId, token)

      // get the user information
      const userPromise = global.getLoggedInUser()
      const [response] = await Promise.all([reportPromise, userPromise])

      return response.report
    }

    if (!global.report && reportId && !loading) {
      setLoading(true)
      getReport()
        .then(report => {
          global.setReport(report)
          global.setChannel(report.channel)
        })

        .catch(err => { global.setError('Error loading report information') })
        .finally(() => setLoading(false))
    }
  }, [global, loading, reportId])

  return (
    <Template subtitle="Success">
      <ChannelHeader channel={global.channel} />
      <Content>
        <ThanksAndUserCapture />
        <hr />
        <ReportInfo report={global.report} />
      </Content>
    </Template>
  )
}

/**
 * Capture email or thank registered user
 * 
 * @param {*} param0 
 * @returns 
 */
function ThanksAndUserCapture() {
  const navigate = useNavigate()
  const global = useContext(GlobalContext)
  const report = global.report
  const user = global.user
  const defaultEmail = user && user.email !== ANONYMOUS_EMAIL ? user.email : ''
  const [email, setEmail] = useState(defaultEmail)

  if (!report || !user) return null

  /**
   * Handle error returned submitting email
   * 
   * @param {*} err 
   */
  const emailError = (err) => {
    if (err.type === "email_used") {
      global.setError("Email address is used by a registered user. Sign in using that email if it is yours.")
    } else if (err.message) {
      global.setError(err.message)
    } else {
      global.setError("Error submitting email address.")
    }
  }

  /**
   * Attach the email specified to the anonymous user
   * 
   * @param {*} e 
   */
  const attachEmail = (e) => {
    e.preventDefault()
    post('register/anonymous/email', { email }, global.getToken())
      .then((response) => {
        global.setToken(response.accessToken)
        global.setUser(response.user)
        navigate('/download')
      })
      .catch(emailError)
  }

  /**
   * Combine the anonymous user with registered user
   */
  const handleAfterLogin = (originalUser) => {
    put('user/anonymous/merge', originalUser, global.getToken())
      .then((response) => navigate('/download'))
      .catch(emailError)
  }

  if (global.user.handle !== ANONYMOUS_HANDLE) {
    return (
      <div>
        <h4>Thanks for your report!</h4>
        <p className="d-flex align-items-center gap-2">
          <UserAvatar user={user} />
          {user.name()}
        </p>
      </div>
    )
  }

  return (
    <div>
      <h4>Thanks for your report!</h4>
      <p className="mb-1">
        Enter your email to receive updates about your issue
        and information about how to join the Trubbl community.
      </p>
      <p style={{ fontSize: 11, marginBottom: 15 }}>
        We'll never share your email without your permission.
      </p>
      <FloatingLabel
        controlId="floatingInput"
        label="Email address"
        className="mb-3"
      >
        <Form.Control
          type="email"
          placeholder="name@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </FloatingLabel>
      <div className="d-flex justify-content-center mb-2">
        <Button
          className='submit-button'
          style={{ marginRight: 10 }}
          variant="primary"
          type="submit"
          onClick={attachEmail}>
          Submit
        </Button>
        <Button
          className='submit-button'
          variant="secondary"
          onClick={() => navigate('/download')}>
          Skip
        </Button>
      </div>

      <div className="d-flex justify-content-center">
        <UserDisplay user={user} afterLogin={handleAfterLogin} />
      </div>
    </div>
  )
}